import { SR_FACE_CHECK_PATH, SR_FACE_DECISION_PATH } from "../sr-constants";
import { parseFaceSearchError, type FaceSearchError } from "./face-search-errors";
import type {
    SrFaceCheckResult,
    SrFaceDecision,
    SrFaceDecisionResult,
    SrFaceMatch,
} from "./sr-face-check-types";

type ApiClient = { post: (path: string, body: object) => Promise<any> };

export class SrFaceCheckApiError extends Error {
    faceError: FaceSearchError;

    constructor(faceError: FaceSearchError) {
        super(faceError.message || faceError.code);
        this.name = "SrFaceCheckApiError";
        this.faceError = faceError;
    }
}

const throwIfFaceCheckFailed = (response: any) => {
    if (!response?.status || response.status === 200) return;
    throw new SrFaceCheckApiError(parseFaceSearchError(response));
};

const unwrapPayload = (response: any): any =>
    response?.data?.payload ??
    response?.data?.result ??
    response?.data?.data ??
    response?.data ??
    response?.payload ??
    response?.result ??
    response;

export const parseFaceCheckResult = (response: any): SrFaceCheckResult => {
    const payload = unwrapPayload(response) || {};
    const matches: SrFaceMatch[] = Array.isArray(payload.matches) ? payload.matches : [];

    return {
        registered: Boolean(payload.registered),
        captured_photo: payload.captured_photo || "",
        decisions: Array.isArray(payload.decisions) ? payload.decisions : ["replace", "keep"],
        matches,
    };
};

export const parseFaceDecisionResult = (response: any): SrFaceDecisionResult => {
    const payload = unwrapPayload(response) || {};
    return {
        ...payload,
        voter: payload.voter || payload,
        photos: Array.isArray(payload.photos) ? payload.photos : [],
        service_requests: Array.isArray(payload.service_requests) ? payload.service_requests : [],
    };
};

export const checkSrFace = async (
    api: ApiClient,
    body: { projectid: number; s3_link: string; voterid?: string }
): Promise<SrFaceCheckResult> => {
    const response = await api.post(SR_FACE_CHECK_PATH, body);
    throwIfFaceCheckFailed(response);
    return parseFaceCheckResult(response);
};

export const decideSrFace = async (
    api: ApiClient,
    body: {
        projectid: number;
        voterid: string;
        s3_link: string;
        decision: SrFaceDecision;
    }
): Promise<SrFaceDecisionResult> => {
    const response = await api.post(SR_FACE_DECISION_PATH, body);
    throwIfFaceCheckFailed(response);
    return parseFaceDecisionResult(response);
};

export const toFaceCheckError = (error: unknown): FaceSearchError => {
    if (error instanceof SrFaceCheckApiError) return error.faceError;
    const anyErr = error as any;
    if (anyErr?.faceError) return anyErr.faceError;
    if (anyErr?.status || anyErr?.data?.code || anyErr?.response) {
        return parseFaceSearchError(anyErr?.response || anyErr);
    }
    return {
        code: "FACE_SEARCH_FAILED",
        message: "Unable to check voter face.",
    };
};
